/**
 * @fileoverview Deep Link Route Definitions
 *
 * Centralized configuration for all deep link routes with validation.
 *
 * @module services/deepLink/routes
 */

import { supabase } from '@/integrations/supabase/client';
import type { DeepLinkRoute, DeepLinkParams, DeepLinkValidationResult } from './types';

/**
 * Cache for route validation to prevent abuse
 */
const validationCache = new Map<string, { result: DeepLinkValidationResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached validation result
 */
function getCachedValidation(cacheKey: string): DeepLinkValidationResult | null {
  const cached = validationCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }
  return null;
}

/**
 * Set validation cache
 */
function setCachedValidation(cacheKey: string, result: DeepLinkValidationResult): void {
  validationCache.set(cacheKey, { result, timestamp: Date.now() });

  // Cleanup old entries
  if (validationCache.size > 100) {
    const now = Date.now();
    for (const [key, value] of validationCache.entries()) {
      if (now - value.timestamp > CACHE_TTL) {
        validationCache.delete(key);
      }
    }
  }
}

/**
 * Deep link route configurations
 * All app routes are prefixed with /app
 */
export const deepLinkRoutes: DeepLinkRoute[] = [
  // App home route
  {
    type: 'home',
    patterns: ['/app', '/app/'],
    requiresAuth: false,
    validate: async () => ({
      isValid: true,
      path: '/app',
      requiresAuth: false,
      routeType: 'home',
    }),
    buildPath: () => '/app',
  },

  // Saved item route
  {
    type: 'saved',
    patterns: ['/app/saved/:id', '/app/saved/*'],
    requiresAuth: true,
    validate: async (params: DeepLinkParams) => {
      // segments[0] = 'app', segments[1] = 'saved', segments[2] = id
      const id = params.segments[2];
      if (!id) {
        return {
          isValid: false,
          errorMessage: 'Invalid saved item link',
          requiresAuth: true,
          routeType: 'saved',
        };
      }

      const cacheKey = `saved:${id}:${params.userId || 'anonymous'}`;
      const cached = getCachedValidation(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase
        .from('saved_items')
        .select('id')
        .eq('id', id)
        .maybeSingle();

      const result: DeepLinkValidationResult = error || !data
        ? {
            isValid: false,
            errorMessage: 'Item not found or no longer available',
            requiresAuth: true,
            routeType: 'saved',
          }
        : {
            isValid: true,
            path: `/app/saved/${id}`,
            requiresAuth: true,
            routeType: 'saved',
            params: { id },
          };

      setCachedValidation(cacheKey, result);
      return result;
    },
    buildPath: (params: DeepLinkParams) => `/app/saved/${params.segments[2]}`,
  },

  // Collection route
  {
    type: 'collection',
    patterns: ['/app/collection/:id', '/app/collection/*'],
    requiresAuth: false,
    validate: async (params: DeepLinkParams) => {
      const id = params.segments[2];
      if (!id) {
        return {
          isValid: false,
          errorMessage: 'Invalid collection link',
          requiresAuth: false,
          routeType: 'collection',
        };
      }

      const cacheKey = `collection:${id}:${params.userId || 'anonymous'}`;
      const cached = getCachedValidation(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase
        .from('collections')
        .select('id, user_id, is_collaborative')
        .eq('id', id)
        .maybeSingle();

      if (error || !data) {
        const result: DeepLinkValidationResult = {
          isValid: false,
          errorMessage: 'Collection not found',
          requiresAuth: false,
          routeType: 'collection',
        };
        setCachedValidation(cacheKey, result);
        return result;
      }

      // Check access permissions
      const isOwner = params.userId && data.user_id === params.userId;
      const isPublic = data.is_collaborative;

      if (!isPublic && !isOwner) {
        const result: DeepLinkValidationResult = {
          isValid: false,
          errorMessage: "You don't have access to this collection",
          requiresAuth: false,
          routeType: 'collection',
        };
        setCachedValidation(cacheKey, result);
        return result;
      }

      const result: DeepLinkValidationResult = {
        isValid: true,
        path: `/app/collection/${id}`,
        requiresAuth: !isPublic,
        routeType: 'collection',
        params: { id },
      };
      setCachedValidation(cacheKey, result);
      return result;
    },
    buildPath: (params: DeepLinkParams) => `/app/collection/${params.segments[2]}`,
  },

  // User profile route
  {
    type: 'user',
    patterns: ['/app/user/:id', '/app/user/*'],
    requiresAuth: false,
    validate: async (params: DeepLinkParams) => {
      const id = params.segments[2];
      if (!id) {
        return {
          isValid: false,
          errorMessage: 'Invalid user profile link',
          requiresAuth: false,
          routeType: 'user',
        };
      }

      const cacheKey = `user:${id}`;
      const cached = getCachedValidation(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', id)
        .maybeSingle();

      const result: DeepLinkValidationResult = error || !data
        ? {
            isValid: false,
            errorMessage: 'User not found',
            requiresAuth: false,
            routeType: 'user',
          }
        : {
            isValid: true,
            path: `/app/user/${id}`,
            requiresAuth: false,
            routeType: 'user',
            params: { id },
          };

      setCachedValidation(cacheKey, result);
      return result;
    },
    buildPath: (params: DeepLinkParams) => `/app/user/${params.segments[2]}`,
  },

  // Vote/Poll route
  {
    type: 'vote',
    patterns: ['/app/vote', '/app/vote?*'],
    requiresAuth: false,
    validate: async (params: DeepLinkParams) => {
      const pollId = params.query.get('poll');

      if (!pollId) {
        // Just navigating to vote tab
        return {
          isValid: true,
          path: '/app/vote',
          requiresAuth: false,
          routeType: 'vote',
        };
      }

      const cacheKey = `vote:${pollId}`;
      const cached = getCachedValidation(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase
        .from('polls')
        .select('id')
        .eq('id', pollId)
        .maybeSingle();

      const result: DeepLinkValidationResult = error || !data
        ? {
            isValid: false,
            errorMessage: 'Poll not found',
            requiresAuth: false,
            routeType: 'vote',
          }
        : {
            isValid: true,
            path: `/app/vote?poll=${pollId}`,
            requiresAuth: false,
            routeType: 'vote',
            params: { poll: pollId },
          };

      setCachedValidation(cacheKey, result);
      return result;
    },
    buildPath: (params: DeepLinkParams) => {
      const pollId = params.query.get('poll');
      return pollId ? `/app/vote?poll=${pollId}` : '/app/vote';
    },
  },

  // Try-on route
  {
    type: 'try-on',
    patterns: ['/app/try-on', '/app/try-on?*'],
    requiresAuth: true,
    validate: async (params: DeepLinkParams) => {
      const lookId = params.query.get('look');

      if (!lookId) {
        // Just navigating to try-on tab
        return {
          isValid: true,
          path: '/app/try-on',
          requiresAuth: true,
          routeType: 'try-on',
        };
      }

      const cacheKey = `try-on:${lookId}`;
      const cached = getCachedValidation(cacheKey);
      if (cached) return cached;

      const { data, error } = await supabase
        .from('tryon_looks')
        .select('id')
        .eq('id', lookId)
        .maybeSingle();

      const result: DeepLinkValidationResult = error || !data
        ? {
            isValid: false,
            errorMessage: 'Try-on look not found',
            requiresAuth: true,
            routeType: 'try-on',
          }
        : {
            isValid: true,
            path: `/app/try-on?look=${lookId}`,
            requiresAuth: true,
            routeType: 'try-on',
            params: { look: lookId },
          };

      setCachedValidation(cacheKey, result);
      return result;
    },
    buildPath: (params: DeepLinkParams) => {
      const lookId = params.query.get('look');
      return lookId ? `/app/try-on?look=${lookId}` : '/app/try-on';
    },
  },

  // Chat route
  {
    type: 'chat',
    patterns: ['/app/chat'],
    requiresAuth: true,
    validate: async () => ({
      isValid: true,
      path: '/app/chat',
      requiresAuth: true,
      routeType: 'chat',
    }),
    buildPath: () => '/app/chat',
  },

  // Settings route
  {
    type: 'settings',
    patterns: ['/app/settings'],
    requiresAuth: true,
    validate: async () => ({
      isValid: true,
      path: '/app/settings',
      requiresAuth: true,
      routeType: 'settings',
    }),
    buildPath: () => '/app/settings',
  },

  // Profile route
  {
    type: 'profile',
    patterns: ['/app/profile'],
    requiresAuth: true,
    validate: async () => ({
      isValid: true,
      path: '/app/profile',
      requiresAuth: true,
      routeType: 'profile',
    }),
    buildPath: () => '/app/profile',
  },
];
